import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Card,
  CardContent,
  CardActions,
  Grid,
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Add from '@mui/icons-material/Add';
import Celebration from '@mui/icons-material/Celebration';
import { useCart } from '../../contexts/CartContext';
import { mockFees, mockFeeCategories } from '../../mock';
import { Fee } from '../../types';

export const CommunityShop: React.FC = () => {
  const { addToCart } = useCart();
  const [selectedFee, setSelectedFee] = useState<Fee | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);

  const publicFees = useMemo(() => {
    return mockFees.filter(
      fee => fee.isActive && (fee.categoryId === '5' || fee.categoryId === '6')
    );
  }, []);

  const getCategoryLabel = (categoryId: string) => {
    const category = mockFeeCategories.find(c => c.id === categoryId);
    return category?.name || 'Other';
  };

  const handleOpenDialog = (fee: Fee) => {
    setSelectedFee(fee);
    setQuantity(1);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFee(null);
    setQuantity(1);
  };

  const handleAddToCart = () => {
    if (selectedFee) {
      addToCart(selectedFee, quantity, 'full');
      handleCloseDialog();
    }
  };

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Celebration sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
              }}
            >
              Community Shop
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
            }}
          >
            Purchase event tickets and other public items
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        <Grid container spacing={3}>
        {publicFees.map(fee => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={fee.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Chip
                  label={getCategoryLabel(fee.categoryId)}
                  size="small"
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  {fee.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {fee.description}
                </Typography>
                <Typography variant="h5" color="primary" fontWeight={600}>
                  ${fee.basePrice.toFixed(2)}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog(fee)}
                >
                  Add to Cart
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

        {publicFees.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="h6" color="text.secondary">
              No public items available at this time
            </Typography>
          </Paper>
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Cart</DialogTitle>
        <DialogContent>
          {selectedFee && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedFee.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedFee.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Chip label={getCategoryLabel(selectedFee.category)} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Price
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight={600}>
                    ${selectedFee.basePrice.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{
                  min: 1,
                  max: selectedFee.maxQuantity || 999,
                }}
                helperText={
                  selectedFee.maxQuantity
                    ? `Maximum quantity: ${selectedFee.maxQuantity}`
                    : undefined
                }
              />

              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Unit Price:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ${selectedFee.basePrice.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Quantity:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {quantity}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    pt: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    Total:
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight={600}>
                    ${(selectedFee.basePrice * quantity).toFixed(2)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToCart}
            startIcon={<ShoppingCart />}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
